import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../config";

// Visual seat picker — loads the event's floor plan and lets the organizer click
// a physical seat, instead of typing a seat number. Assigns through the seating
// app so the live seating chart updates (same behaviour as the mobile app).
export default function SeatPickerModal({
  isOpen,
  onClose,
  eventId,
  guest,
  onAssigned,
  showSuccessModal,
  showFailureModal,
}) {
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !eventId) return;
    setSelected(null);
    setLoading(true);
    const token = localStorage.getItem("access_token");
    axios
      .get(`${config.API_BASE_URL}/seating/events/${eventId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setLayout(r.data?.layout || null))
      .catch(() => setLayout(null))
      .finally(() => setLoading(false));
  }, [isOpen, eventId]);

  if (!isOpen) return null;

  // recipient/guest can be a numeric id, a numeric string, or a nested user
  // object (sometimes WITHOUT an id). Only treat it as a user id when it's a real
  // number; otherwise we assign the seat by name.
  const numId = (v) => {
    if (typeof v === "number") return v;
    if (typeof v === "string" && /^\d+$/.test(v)) return Number(v);
    if (v && typeof v === "object" && typeof v.id === "number") return v.id;
    return null;
  };
  const objName = (o) =>
    o && typeof o === "object"
      ? `${o.first_name || ""} ${o.last_name || ""}`.trim() || o.name || o.email || null
      : null;

  const userId = numId(guest?.recipient) ?? numId(guest?.guest) ?? null;
  const guestName =
    guest?.name ||
    guest?.recipient_name ||
    objName(guest?.recipient) ||
    objName(guest?.guest) ||
    guest?.email ||
    guest?.recipient_email ||
    "guest";

  const assign = async () => {
    if (!selected) return;
    setSaving(true);
    const token = localStorage.getItem("access_token");
    try {
      await axios.post(
        `${config.API_BASE_URL}/seating/seats/${selected.id}/assign/`,
        userId ? { user_id: userId } : { name: guestName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccessModal && showSuccessModal("Seat assigned");
      if (onAssigned) await onAssigned();
      onClose();
    } catch (e) {
      showFailureModal &&
        showFailureModal(e.response?.data?.detail || "Couldn't assign the seat");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            Assign a seat for {guestName}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Tap a seat. <span className="text-gray-600 dark:text-gray-300">Grey</span> = free,{" "}
            <span className="text-green-600">green</span> = taken. Assigning a taken seat moves the
            current occupant off it.
          </p>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-5">
          {loading ? (
            <div className="esawa-sweepbar" />
          ) : !layout ? (
            <p className="text-gray-500 dark:text-gray-400">
              No seating plan for this event yet. Create one in the <strong>Seating</strong> tab
              first, then assign seats here.
            </p>
          ) : (
            (layout.tables || []).map((table) => (
              <div key={table.id}>
                <div className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  {table.name}
                </div>
                <div className="flex flex-wrap gap-2">
                  {(table.seats || []).map((seat) => {
                    const taken = seat.status !== "available" || !!seat.assigned_user_name;
                    const isSel = selected?.id === seat.id;
                    return (
                      <button
                        key={seat.id}
                        type="button"
                        onClick={() => setSelected(seat)}
                        title={seat.assigned_user_name || "Free"}
                        className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all
                          ${isSel ? "border-blue-600 ring-2 ring-blue-300" : "border-transparent"}
                          ${
                            taken
                              ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200"
                          }`}
                      >
                        {seat.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}

          {selected && selected.assigned_user_name && (
            <p className="text-sm text-amber-600">
              Seat {selected.label} is taken by {selected.assigned_user_name}. Assigning will move
              them off it.
            </p>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={assign}
            disabled={!selected || saving}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Assigning…" : "Assign seat"}
          </button>
        </div>
      </div>
    </div>
  );
}
